<template>
  <main class="warning-page" :style="{ backgroundImage: `url(${backgroundImage})` }">
    <header class="warning-header">
      <button type="button" class="warning-back" @click="$router.push('/')">
        <span aria-hidden="true">←</span>
        返回大屏
      </button>
      <h1 class="warning-title">预警中心</h1>
    </header>

    <section class="warning-columns" aria-label="预警信息列表">
      <article v-for="column in columns" :key="column.key" class="warning-column">
        <header class="warning-column__header">
          <div>
            <span>{{ column.eyebrow }}</span>
            <h2>{{ column.title }}</h2>
          </div>
          <strong>{{ column.total }}</strong>
        </header>

        <div class="warning-list">
          <template v-if="loading[column.key]">
            <div v-for="index in pageSize" :key="`${column.key}-skeleton-${index}`" class="warning-skeleton">
              <i></i><i></i><i></i>
            </div>
          </template>

          <template v-else>
            <button
              v-for="item in column.rows"
              :key="`${column.key}-${item.id}`"
              type="button"
              class="warning-row"
              @click="openDetail(column, item)"
            >
              <div class="warning-row__top">
                <span v-if="column.disposal" class="warning-status" :class="`warning-status--${item.handlingStatus === '1' ? 'done' : 'pending'}`">
                  {{ item.handlingStatus === '1' ? '办理完成' : '待办理' }}
                </span>
                <span v-if="column.disposal && item.urgentStatus === '1'" class="warning-urgent">紧急</span>
                <time>{{ formatDateTime(item.publishTime) }}</time>
              </div>
              <strong>{{ item.title || '未填写标题' }}</strong>
              <p>{{ item.content || '暂无正文' }}</p>
              <div class="warning-row__meta">
                <span>{{ item.publishDepartment || '未填写发布部门' }}</span>
                <span v-if="column.disposal">{{ item.todoDepartments || item.todoDepartment || '未填写待办部门' }}</span>
              </div>
            </button>
            <div v-if="!column.rows.length" class="warning-empty">暂无数据</div>
          </template>
        </div>

        <footer class="warning-column__pagination" :aria-label="`${column.title}分页`">
          <button
            type="button"
            :disabled="column.pageNum <= 1 || loading[column.key]"
            @click="changePage(column.key, column.pageNum - 1)"
          >上一页</button>
          <button
            v-for="page in visiblePages(column)"
            :key="`${column.key}-page-${page}`"
            type="button"
            class="warning-page-btn"
            :class="{ active: page === column.pageNum }"
            :disabled="loading[column.key]"
            @click="changePage(column.key, page)"
          >{{ page }}</button>
          <button
            type="button"
            :disabled="column.pageNum >= column.totalPages || loading[column.key]"
            @click="changePage(column.key, column.pageNum + 1)"
          >下一页</button>
          <span>每页 {{ pageSize }} 条</span>
        </footer>
      </article>
    </section>

    <div v-if="errorMessage" class="warning-error">{{ errorMessage }}</div>

    <div v-if="detail.visible" class="warning-detail-layer" @click.self="closeDetail">
      <section class="warning-detail" role="dialog" aria-modal="true" :aria-label="detail.title">
        <header class="warning-detail__header">
          <div>
            <span>{{ detail.moduleTitle }}</span>
            <h2>{{ detail.title }}</h2>
          </div>
          <button type="button" aria-label="关闭" @click="closeDetail">×</button>
        </header>

        <div class="warning-detail__body">
          <div class="warning-detail__facts">
            <template v-if="detail.disposal">
              <div><span>待办部门</span><strong>{{ detail.item.todoDepartments || detail.item.todoDepartment || '' }}</strong></div>
              <div><span>办理时限</span><strong>{{ formatDateTime(detail.item.handlingDeadline) }}</strong></div>
              <div><span>是否紧急</span><strong>{{ detail.item.urgentStatus === '1' ? '是' : '否' }}</strong></div>
              <div><span>办理状态</span><strong>{{ detail.item.handlingStatus === '1' ? '办理完成' : '待办理' }}</strong></div>
            </template>
            <div><span>发布时间</span><strong>{{ formatDateTime(detail.item.publishTime) }}</strong></div>
            <div><span>发布部门</span><strong>{{ detail.item.publishDepartment || '' }}</strong></div>
          </div>

          <section class="warning-detail__section">
            <h3>正文</h3>
            <p>{{ detail.item.content || '暂无正文' }}</p>
          </section>

          <template v-if="detail.disposal">
            <section class="warning-detail__section">
              <div class="warning-detail__section-title">
                <h3>领导批示</h3>
                <button v-if="canAddLeader" type="button" @click="openLeaderDialog">新增批示</button>
              </div>
              <div v-if="detail.leaderInstructions.length" class="warning-detail__records">
                <article v-for="(instruction, index) in detail.leaderInstructions" :key="`instruction-${index}`">
                  <div class="warning-record__head warning-record__head--deletable"><strong>{{ leaderRecordName(instruction) }}</strong><time>{{ leaderRecordTime(instruction) }}</time></div>
                  <p>{{ leaderRecordContent(instruction) || '未填写批示内容' }}</p>
                  <button
                    v-if="canDeleteLeader"
                    type="button"
                    class="warning-record-delete"
                    :disabled="deletingLeaderIndex === index"
                    @click="deleteLeaderInstruction(index)"
                  >{{ deletingLeaderIndex === index ? '删除中...' : '删除' }}</button>
                </article>
              </div>
              <div v-else class="warning-detail__empty">暂无领导批示</div>
            </section>

            <section class="warning-detail__section">
              <div class="warning-detail__section-title">
                <h3>部门办理</h3>
                <button v-if="canAddHandling" type="button" @click="openHandlingDialog">提交办理</button>
              </div>
              <div v-if="detail.departmentHandling.length" class="warning-detail__records">
                <article v-for="(handling, index) in detail.departmentHandling" :key="`handling-${index}`">
                  <div><strong>{{ handling.handler || handling.processor || handling.name || '处理人' }}</strong><time>{{ formatWorkflowTime(handling.handleTime || handling.time || handling.createTime || '') }}</time></div>
                  <dl>
                    <dt>处理结果</dt><dd>{{ handling.result || '' }}</dd>
                    <dt>过程</dt><dd>{{ handling.process || handling.procedure || '' }}</dd>
                    <dt>附件</dt>
                    <dd>
                      <template v-if="normalizeAttachments(handling.attachments).length">
                        <a v-for="(attachment, attachmentIndex) in normalizeAttachments(handling.attachments)" :key="attachmentIndex" :href="normalizeAttachmentUrl(attachment)" target="_blank" rel="noopener noreferrer">附件{{ attachmentIndex + 1 }}</a>
                      </template>
                      <span v-else>无</span>
                    </dd>
                  </dl>
                </article>
              </div>
              <div v-else class="warning-detail__empty">暂无部门办理记录</div>
            </section>
          </template>
        </div>
      </section>
    </div>

    <div v-if="leaderForm.open" class="warning-workflow-layer" @pointerdown.self="closeLeaderDialog()">
      <section class="warning-workflow-dialog" role="dialog" aria-modal="true" aria-label="新增领导批示">
        <header class="warning-workflow-dialog__header">
          <h3>新增领导批示</h3>
          <button type="button" aria-label="关闭" @click="closeLeaderDialog">×</button>
        </header>
        <form class="warning-workflow-form" @submit.prevent="submitLeaderInstruction">
          <textarea v-model="leaderForm.instruction" rows="5" maxlength="1000" placeholder="请输入领导批示内容"></textarea>
          <p v-if="leaderForm.error" class="warning-workflow-error">{{ leaderForm.error }}</p>
          <div class="warning-workflow-actions">
            <button type="button" class="warning-workflow-cancel" @click="closeLeaderDialog">取消</button>
            <button type="submit" :disabled="leaderForm.submitting">{{ leaderForm.submitting ? '提交中...' : '确认批示' }}</button>
          </div>
        </form>
      </section>
    </div>

    <div v-if="handlingForm.open" class="warning-workflow-layer" @pointerdown.self="closeHandlingDialog()">
      <section class="warning-workflow-dialog warning-workflow-dialog--handling" role="dialog" aria-modal="true" aria-label="提交部门办理">
        <header class="warning-workflow-dialog__header">
          <h3>提交部门办理</h3>
          <button type="button" aria-label="关闭" @click="closeHandlingDialog">×</button>
        </header>
        <form class="warning-workflow-form" @submit.prevent="submitDepartmentHandling">
          <input v-model="handlingForm.result" maxlength="500" placeholder="请输入处理结果">
          <textarea v-model="handlingForm.process" rows="6" maxlength="2000" wrap="soft" placeholder="请输入处理过程"></textarea>
          <div class="warning-upload-row">
            <label>
              <span>{{ handlingForm.uploading ? '上传中...' : '添加附件' }}</span>
              <input type="file" multiple :disabled="handlingForm.uploading" @change="uploadAttachments">
            </label>
            <button v-for="(attachment, index) in handlingForm.attachments" :key="attachment" type="button" @click="removeAttachment(index)">
              {{ attachmentName(attachment) }} ×
            </button>
          </div>
          <label class="warning-complete-check"><input v-model="handlingForm.completed" type="checkbox"> 提交后标记为办理完成</label>
          <p v-if="handlingForm.error" class="warning-workflow-error">{{ handlingForm.error }}</p>
          <div class="warning-workflow-actions">
            <button type="button" class="warning-workflow-cancel" @click="closeHandlingDialog">取消</button>
            <button type="submit" :disabled="handlingForm.submitting || handlingForm.uploading">{{ handlingForm.submitting ? '提交中...' : '确认提交' }}</button>
          </div>
        </form>
      </section>
    </div>
  </main>
</template>

<script>
import {
  addYjDepartmentHandling,
  addYjLeaderInstruction,
  deleteYjLeaderInstruction,
  getYjBusinessList,
  getYjChangeList,
  getYjRectificationList,
  uploadYjAttachment
} from '@/api/datav'
import { getUserProfile } from '@/utils/auth'

function parseJsonArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch (error) {
    return []
  }
}

const listFetchers = {
  business: getYjBusinessList,
  change: getYjChangeList,
  rectification: getYjRectificationList
}

export default {
  name: 'NoticeView',
  data() {
    return {
      backgroundImage: require('@/assets/bj.png'),
      pageSize: 5,
      pageNums: {
        business: 1,
        change: 1,
        rectification: 1
      },
      loading: {
        business: false,
        change: false,
        rectification: false
      },
      errorMessage: '',
      userProfile: getUserProfile(),
      datasets: {
        business: { rows: [], total: 0 },
        change: { rows: [], total: 0 },
        rectification: { rows: [], total: 0 }
      },
      detail: {
        visible: false,
        moduleTitle: '',
        title: '',
        disposal: false,
        item: {},
        leaderInstructions: [],
        departmentHandling: []
      },
      leaderForm: {
        open: false,
        instruction: '',
        submitting: false,
        error: ''
      },
      deletingLeaderIndex: null,
      handlingForm: {
        open: false,
        result: '',
        process: '',
        attachments: [],
        completed: false,
        uploading: false,
        submitting: false,
        error: ''
      }
    }
  },
  computed: {
    columns() {
      return [
        { key: 'business', title: '业务预警处置单', eyebrow: '业务预警', disposal: true, ...this.datasets.business, pageNum: this.pageNums.business, totalPages: this.totalPagesFor(this.datasets.business.total) },
        { key: 'change', title: '异动情况', eyebrow: '运行异动', disposal: true, ...this.datasets.change, pageNum: this.pageNums.change, totalPages: this.totalPagesFor(this.datasets.change.total) },
        { key: 'rectification', title: '核查整改处置单', eyebrow: '核查整改', disposal: true, ...this.datasets.rectification, pageNum: this.pageNums.rectification, totalPages: this.totalPagesFor(this.datasets.rectification.total) }
      ]
    },
    canAddLeader() {
      const roles = this.userProfile.roles || []
      return this.detail.disposal && (roles.includes('lpds') || roles.includes('admin'))
    },
    canDeleteLeader() {
      return this.canAddLeader
    },
    canAddHandling() {
      if (!this.detail.disposal) return false
      if (Number(this.userProfile.userId) === 1) return true
      const rawIds = this.detail.item.todoDeptIds || this.detail.item.todoDeptId || ''
      const deptIds = String(rawIds).split(',').map(value => Number(value)).filter(value => Number.isFinite(value))
      return deptIds.includes(Number(this.userProfile.deptId))
    }
  },
  created() {
    this.fetchAllData()
  },
  methods: {
    totalPagesFor(total) {
      return Math.max(1, Math.ceil(Number(total) / this.pageSize))
    },
    visiblePages(column) {
      const start = Math.max(1, Math.min(column.pageNum - 2, column.totalPages - 4))
      const end = Math.min(column.totalPages, start + 4)
      return Array.from({ length: end - start + 1 }, (_, index) => start + index)
    },
    async fetchAllData() {
      this.errorMessage = ''
      await Promise.all(Object.keys(listFetchers).map(key => this.fetchColumn(key, 1)))
    },
    async fetchColumn(key, page) {
      const request = listFetchers[key]
      if (!request) return
      this.$set(this.loading, key, true)
      try {
        const result = await request(page, this.pageSize)
        const totalPages = this.totalPagesFor(result.total)
        const nextPage = Math.max(1, Math.min(page, totalPages))
        if (nextPage !== page) {
          await this.fetchColumn(key, nextPage)
          return
        }
        this.$set(this.datasets, key, result)
        this.$set(this.pageNums, key, nextPage)
      } catch (error) {
        this.errorMessage = error.message || '预警数据加载失败'
      } finally {
        this.$set(this.loading, key, false)
      }
    },
    changePage(key, page) {
      const dataset = this.datasets[key]
      const nextPage = Math.max(1, Math.min(page, this.totalPagesFor(dataset.total)))
      if (nextPage === this.pageNums[key] || this.loading[key]) return
      this.fetchColumn(key, nextPage)
    },
    openDetail(column, item) {
      this.resetWorkflowForms()
      this.detail = {
        visible: true,
        type: column.key,
        moduleTitle: column.title,
        title: item.title || '未填写标题',
        disposal: column.disposal,
        item,
        leaderInstructions: parseJsonArray(item.leaderInstructions),
        departmentHandling: parseJsonArray(item.departmentHandling)
      }
    },
    closeDetail() {
      this.detail.visible = false
      this.resetWorkflowForms()
    },
    resetWorkflowForms() {
      this.leaderForm = { open: false, instruction: '', submitting: false, error: '' }
      this.handlingForm = { open: false, result: '', process: '', attachments: [], completed: false, uploading: false, submitting: false, error: '' }
      this.deletingLeaderIndex = null
    },
    openLeaderDialog() {
      this.leaderForm.error = ''
      this.leaderForm.open = true
    },
    closeLeaderDialog(force = false) {
      if (!force && this.leaderForm.submitting) return
      this.leaderForm = { open: false, instruction: '', submitting: false, error: '' }
    },
    openHandlingDialog() {
      this.handlingForm.error = ''
      this.handlingForm.open = true
    },
    closeHandlingDialog(force = false) {
      if (!force && (this.handlingForm.submitting || this.handlingForm.uploading)) return
      this.handlingForm = { open: false, result: '', process: '', attachments: [], completed: false, uploading: false, submitting: false, error: '' }
    },
    applyUpdatedDetail(item) {
      const type = this.detail.type
      const rows = this.datasets[type] && this.datasets[type].rows
      const index = Array.isArray(rows) ? rows.findIndex(row => row.id === item.id) : -1
      if (index >= 0) this.$set(rows, index, item)
      this.detail.item = item
      this.detail.leaderInstructions = parseJsonArray(item.leaderInstructions)
      this.detail.departmentHandling = parseJsonArray(item.departmentHandling)
    },
    async submitLeaderInstruction() {
      this.leaderForm.error = ''
      const instruction = String(this.leaderForm.instruction || '').trim()
      if (!instruction) {
        this.leaderForm.error = '请输入批示内容'
        return
      }
      this.leaderForm.submitting = true
      try {
        const item = await addYjLeaderInstruction(this.detail.type, this.detail.item.id, instruction)
        this.applyUpdatedDetail(item)
        this.closeLeaderDialog(true)
      } catch (error) {
        this.leaderForm.error = error.message || '批示提交失败'
      } finally {
        this.leaderForm.submitting = false
      }
    },
    async deleteLeaderInstruction(index) {
      if (this.deletingLeaderIndex !== null || !window.confirm('确认删除这条领导批示吗？')) return
      this.deletingLeaderIndex = index
      try {
        const item = await deleteYjLeaderInstruction(this.detail.type, this.detail.item.id, index)
        this.applyUpdatedDetail(item)
      } catch (error) {
        this.errorMessage = error.message || '领导批示删除失败'
      } finally {
        this.deletingLeaderIndex = null
      }
    },
    async uploadAttachments(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length) return
      if (this.handlingForm.attachments.length + files.length > 10) {
        this.handlingForm.error = '附件不能超过10个'
        return
      }
      this.handlingForm.error = ''
      this.handlingForm.uploading = true
      try {
        for (const file of files) {
          const result = await uploadYjAttachment(file)
          this.handlingForm.attachments.push(result.fileName || result.url)
        }
      } catch (error) {
        this.handlingForm.error = error.message || '附件上传失败'
      } finally {
        this.handlingForm.uploading = false
      }
    },
    removeAttachment(index) {
      this.handlingForm.attachments.splice(index, 1)
    },
    attachmentName(value) {
      return String(value || '').split('/').pop() || '附件'
    },
    leaderRecordName(record) {
      if (!record || typeof record !== 'object') return '领导'
      return record.leader || record.leaderName || record.name || record.userName || record.operator || '领导'
    },
    leaderRecordTime(record) {
      if (!record || typeof record !== 'object') return ''
      return this.formatWorkflowTime(record.time || record.instructionTime || record.createTime || record.createdAt || record.date || '')
    },
    leaderRecordContent(record) {
      if (record === null || record === undefined) return ''
      if (typeof record !== 'object') return String(record)
      const content = record.instruction || record.content || record.comment || record.leaderInstruction || record.leaderOpinion || record.opinion || record.remark || record.text || record.value
      if (content === null || content === undefined) return ''
      return typeof content === 'object' ? JSON.stringify(content) : String(content)
    },
    formatWorkflowTime(value) {
      const text = String(value || '').trim()
      if (/^\d{14}$/.test(text)) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}:${text.slice(12, 14)}`
      }
      if (/^\d{12}$/.test(text)) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}`
      }
      return text.replace('T', ' ').slice(0, 19)
    },
    async submitDepartmentHandling() {
      this.handlingForm.error = ''
      const result = String(this.handlingForm.result || '').trim()
      const process = String(this.handlingForm.process || '').trim()
      if (!result || !process) {
        this.handlingForm.error = '请填写处理结果和处理过程'
        return
      }
      this.handlingForm.submitting = true
      try {
        const item = await addYjDepartmentHandling(this.detail.type, this.detail.item.id, {
          result,
          process,
          attachments: this.handlingForm.attachments,
          completed: this.handlingForm.completed
        })
        this.applyUpdatedDetail(item)
        this.closeHandlingDialog(true)
      } catch (error) {
        this.handlingForm.error = error.message || '办理记录提交失败'
      } finally {
        this.handlingForm.submitting = false
      }
    },
    formatDateTime(value) {
      return this.formatWorkflowTime(value).slice(0, 16)
    },
    normalizeAttachments(value) {
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    },
    normalizeAttachmentUrl(value) {
      const attachment = typeof value === 'string' ? value : (value.url || value.path || '')
      if (!attachment) return '#'
      if (/^(https?:)?\/\//.test(attachment)) return attachment
      const normalized = attachment.startsWith('/') ? attachment : `/${attachment}`
      return normalized.startsWith('/prod-api/') ? normalized : `/prod-api${normalized}`
    }
  }
}
</script>

<style scoped>
.warning-page {
  --warning-page-padding-x: clamp(22px, 2.2vw, 42px);
  --warning-page-padding-top: clamp(18px, 2.2vh, 30px);
  --warning-page-padding-bottom: clamp(16px, 1.8vh, 24px);
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 1.4vh;
  width: 100vw;
  height: 100vh;
  padding: var(--warning-page-padding-top) var(--warning-page-padding-x) var(--warning-page-padding-bottom);
  overflow: hidden;
  color: #f3fbff;
  background-color: #0b2d57;
  background-position: center top;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.warning-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(38, 118, 180, 0.16);
  pointer-events: none;
}

.warning-header,
.warning-columns,
.warning-pagination,
.warning-error {
  position: relative;
  z-index: 1;
}

.warning-header {
  position: relative;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  height: clamp(72px, 8.2vh, 92px);
}

.warning-back {
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 7;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: clamp(34px, 1.9vw, 42px);
  padding: 0 clamp(12px, 0.8vw, 18px);
  border: 1px solid rgba(72, 201, 255, 0.48);
  border-radius: clamp(5px, 0.3vw, 7px);
  color: #dff7ff;
  font-size: clamp(12px, 0.7vw, 15px);
  line-height: 1;
  background: linear-gradient(180deg, rgba(7, 93, 211, 0.9), rgba(4, 58, 122, 0.78));
  box-shadow: inset 0 0 10px rgba(91, 228, 255, 0.25);
  transform: translateY(-50%);
  cursor: pointer;
}

.warning-back span {
  font-size: 20px;
}

.warning-title {
  margin: 0;
  color: #effcff;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  font-size: clamp(36px, 3vw, 60px);
  font-style: italic;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1;
  -webkit-text-stroke: 1px rgba(238, 252, 255, 0.86);
  text-shadow:
    0 2px 0 #84c7e2,
    3px 5px 0 #125273,
    6px 8px 4px rgba(0, 19, 46, 0.78),
    0 0 16px rgba(68, 210, 255, 0.62);
  transform: skewX(-5deg);
}

.warning-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(16px, 1.35vw, 28px);
  min-height: 0;
}

.warning-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: clamp(14px, 1vw, 20px);
  border: 1px solid rgba(75, 191, 255, 0.42);
  border-radius: 8px;
  background: rgba(13, 66, 119, 0.72);
  box-shadow: inset 0 0 1.6vw rgba(41, 161, 238, 0.08);
}

.warning-column__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-end;
  justify-content: space-between;
  min-height: 6.5vh;
  padding: 0 0.2vw 1.1vh;
  border-bottom: 1px solid rgba(82, 198, 255, 0.26);
}

.warning-column__header span {
  color: #6bdfff;
  font-size: clamp(11px, 0.6vw, 13px);
}

.warning-column__header h2 {
  margin: 4px 0 0;
  font-size: clamp(18px, 1.12vw, 24px);
  letter-spacing: 0;
}

.warning-column__header > strong {
  color: #4bddff;
  font-size: clamp(25px, 1.6vw, 34px);
}

.warning-list {
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 0.72vh;
  flex: 1;
  min-height: 0;
  padding-top: 0.9vh;
}

.warning-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  padding: 0.75vh 0.78vw;
  border: 1px solid rgba(73, 181, 239, 0.18);
  border-radius: 6px;
  color: #eefaff;
  text-align: left;
  background: rgba(15, 81, 145, 0.58);
  cursor: pointer;
  transition: border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease;
}

.warning-row:hover {
  border-color: rgba(78, 216, 255, 0.62);
  background: rgba(21, 105, 178, 0.76);
  transform: translateY(-1px);
}

.warning-row__top,
.warning-row__meta {
  display: flex;
  align-items: center;
  gap: 0.38vw;
  min-width: 0;
}

.warning-row__top time {
  margin-left: auto;
  color: rgba(184, 222, 244, 0.72);
  font-size: clamp(10px, 0.55vw, 12px);
}

.warning-status,
.warning-urgent {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 7px;
  border-radius: 4px;
  font-size: clamp(10px, 0.54vw, 12px);
}

.warning-status--pending {
  color: #ffe19b;
  background: rgba(187, 119, 18, 0.28);
}

.warning-status--done {
  color: #78f0bb;
  background: rgba(17, 151, 101, 0.25);
}

.warning-urgent {
  color: #ffd3d3;
  background: rgba(201, 55, 65, 0.3);
}

.warning-row > strong {
  margin-top: 0.52vh;
  overflow: hidden;
  font-size: clamp(13px, 0.75vw, 16px);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.warning-row > p {
  display: -webkit-box;
  margin: 0.42vh 0 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  color: rgba(213, 237, 250, 0.75);
  font-size: clamp(11px, 0.62vw, 13px);
  line-height: 1.45;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.warning-row__meta {
  justify-content: space-between;
  margin-top: 0.5vh;
  color: #77d9f7;
  font-size: clamp(10px, 0.56vw, 12px);
}

.warning-skeleton {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.7vh;
  padding: 0 0.8vw;
  border: 1px solid rgba(73, 181, 239, 0.12);
  border-radius: 6px;
  background: rgba(16, 78, 138, 0.42);
}

.warning-skeleton i {
  display: block;
  width: 72%;
  height: 8px;
  border-radius: 3px;
  background: rgba(103, 188, 228, 0.2);
}

.warning-skeleton i:nth-child(2) { width: 92%; }
.warning-skeleton i:nth-child(3) { width: 48%; }

.warning-empty {
  grid-row: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(199, 231, 248, 0.66);
  font-size: 14px;
}

.warning-column__pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 34px;
  margin-top: 1.1vh;
  padding-top: 1vh;
  border-top: 1px solid rgba(104, 210, 255, 0.2);
}

.warning-column__pagination button {
  min-width: 38px;
  height: 34px;
  padding: 0 11px;
  border: 1px solid rgba(115, 218, 255, 0.46);
  border-radius: 6px;
  color: #e6f8ff;
  background: rgba(19, 97, 169, 0.78);
  cursor: pointer;
}

.warning-column__pagination button.active {
  border-color: #5ce1ff;
  background: #168bd6;
}

.warning-column__pagination button:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.warning-column__pagination > span {
  margin-left: 8px;
  color: rgba(190, 229, 249, 0.72);
  font-size: 12px;
}

.warning-error {
  position: fixed;
  left: 50%;
  bottom: 8vh;
  padding: 9px 14px;
  border: 1px solid rgba(255, 126, 126, 0.46);
  border-radius: 6px;
  color: #ffd6d6;
  background: rgba(119, 20, 31, 0.88);
  transform: translateX(-50%);
}

.warning-detail-layer {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(1, 10, 29, 0.68);
}

.warning-detail {
  display: flex;
  flex-direction: column;
  width: min(940px, 78vw);
  max-height: 84vh;
  border: 1px solid rgba(74, 207, 255, 0.56);
  border-radius: 8px;
  background: #052d68;
  box-shadow: 0 22px 70px rgba(0, 8, 35, 0.6), inset 0 0 32px rgba(54, 174, 244, 0.08);
}

.warning-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(78, 193, 249, 0.24);
}

.warning-detail__header span {
  color: #6bdbfb;
  font-size: 12px;
}

.warning-detail__header h2 {
  margin: 4px 0 0;
  font-size: 23px;
  letter-spacing: 0;
}

.warning-detail__header button {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(106, 216, 255, 0.34);
  border-radius: 50%;
  color: #e4f9ff;
  font-size: 25px;
  background: rgba(14, 94, 175, 0.52);
  cursor: pointer;
}

.warning-detail__body {
  padding: 20px 22px 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(83, 202, 244, 0.76) rgba(2, 34, 82, 0.36);
}

.warning-detail__body::-webkit-scrollbar {
  width: 8px;
}

.warning-detail__body::-webkit-scrollbar-track {
  margin: 8px 0;
  border-radius: 8px;
  background: rgba(2, 34, 82, 0.36);
}

.warning-detail__body::-webkit-scrollbar-thumb {
  min-height: 48px;
  border: 2px solid rgba(2, 34, 82, 0.36);
  border-radius: 8px;
  background: rgba(83, 202, 244, 0.76);
}

.warning-detail__facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 18px;
}

.warning-detail__facts div {
  padding: 10px 12px;
  border-left: 2px solid #25cfff;
  background: rgba(7, 66, 137, 0.58);
}

.warning-detail__facts span,
.warning-detail__facts strong {
  display: block;
}

.warning-detail__facts span {
  color: rgba(176, 220, 243, 0.72);
  font-size: 11px;
}

.warning-detail__facts strong {
  margin-top: 4px;
  overflow-wrap: anywhere;
  font-size: 14px;
  word-break: break-word;
}

.warning-detail__section {
  margin-top: 18px;
}

.warning-detail__section h3 {
  margin: 0 0 9px;
  color: #6bdfff;
  font-size: 15px;
}

.warning-detail__section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
}

.warning-detail__section-title h3 {
  margin: 0;
}

.warning-detail__section-title button,
.warning-workflow-actions button {
  min-height: 30px;
  padding: 0 12px;
  border: 1px solid rgba(91, 218, 255, 0.55);
  border-radius: 5px;
  color: #e9fbff;
  background: rgba(8, 112, 190, 0.8);
  cursor: pointer;
}

.warning-workflow-form {
  display: grid;
  gap: 10px;
  min-width: 0;
  min-height: 0;
  margin: 0;
  padding: 18px 20px 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.warning-workflow-form input:not([type="checkbox"]),
.warning-workflow-form textarea {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 9px 11px;
  border: 1px solid rgba(98, 199, 244, 0.36);
  border-radius: 5px;
  outline: none;
  color: #eefaff;
  font: inherit;
  background: rgba(3, 27, 67, 0.78);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.warning-workflow-form textarea {
  min-height: 180px;
  max-height: min(460px, 52vh);
  overflow-x: hidden;
  overflow-y: auto;
  resize: vertical;
  white-space: pre-wrap;
}

.warning-workflow-form input:focus,
.warning-workflow-form textarea:focus {
  border-color: #5ce1ff;
}

.warning-workflow-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.warning-workflow-actions button:disabled {
  opacity: 0.5;
  cursor: wait;
}

.warning-workflow-error {
  margin: 0;
  color: #ffb7b7;
  font-size: 12px;
}

.warning-workflow-cancel {
  background: rgba(43, 88, 141, 0.68) !important;
}

.warning-upload-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.warning-upload-row label,
.warning-upload-row button {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(91, 202, 255, 0.38);
  border-radius: 5px;
  color: #ccefff;
  font-size: 12px;
  background: rgba(5, 67, 145, 0.62);
  cursor: pointer;
}

.warning-upload-row button {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.warning-upload-row input[type="file"] {
  display: none;
}

.warning-complete-check {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #d9f3ff;
  font-size: 13px;
}

.warning-detail__section > p {
  margin: 0;
  color: #e7f6fd;
  font-size: 14px;
  line-height: 1.8;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
}

.warning-detail__records {
  display: grid;
  gap: 8px;
}

.warning-detail__records article {
  position: relative;
  padding: 12px 14px;
  border: 1px solid rgba(72, 181, 237, 0.2);
  border-radius: 6px;
  background: rgba(4, 45, 103, 0.54);
}

.warning-detail__records article > div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  color: #ffffff;
}

.warning-record__head--deletable {
  padding-right: 60px;
}

.warning-detail__records time {
  color: rgba(180, 222, 243, 0.68);
  font-size: 12px;
}

.warning-detail__records p {
  margin: 8px 0 0;
  color: #dfedf6;
  overflow-wrap: anywhere;
  line-height: 1.65;
  word-break: break-word;
  white-space: pre-wrap;
}

.warning-record-delete {
  position: absolute;
  top: 10px;
  right: 12px;
  min-width: 44px;
  height: 26px;
  padding: 0 7px;
  border: 1px solid rgba(255, 141, 141, 0.6);
  border-radius: 4px;
  color: #ffd0d0;
  font-size: 12px;
  background: rgba(122, 32, 49, 0.52);
  cursor: pointer;
}

.warning-record-delete:disabled {
  opacity: 0.55;
  cursor: wait;
}

.warning-detail__records dl {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 6px 10px;
  margin: 10px 0 0;
  font-size: 13px;
}

.warning-detail__records dt { color: #78d9f7; }
.warning-detail__records dd { margin: 0; color: #e8f5fb; overflow-wrap: anywhere; word-break: break-word; white-space: pre-wrap; }
.warning-detail__records a { margin-right: 10px; color: #67e2ff; }
.warning-detail__empty { color: rgba(196, 228, 245, 0.64); font-size: 13px; }

.warning-workflow-layer {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  padding: clamp(32px, 7vh, 72px) 24px 24px;
  background: rgba(1, 10, 29, 0.46);
}

.warning-workflow-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 56px);
  min-width: 0;
  margin: 0 auto;
  overflow: hidden;
  width: min(680px, 100%);
  border: 1px solid rgba(82, 209, 255, 0.62);
  border-radius: 8px;
  background: #052d68;
  box-shadow: 0 22px 70px rgba(0, 8, 35, 0.62), inset 0 0 28px rgba(54, 174, 244, 0.08);
}

.warning-workflow-dialog--handling {
  width: min(760px, 100%);
}

.warning-workflow-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(78, 193, 249, 0.24);
}

.warning-workflow-dialog__header h3 {
  margin: 0;
  color: #6bdbfb;
  font-size: 17px;
}

.warning-workflow-dialog__header button {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(106, 216, 255, 0.34);
  border-radius: 50%;
  color: #e4f9ff;
  font-size: 21px;
  line-height: 1;
  background: rgba(14, 94, 175, 0.52);
  cursor: pointer;
}

@media (max-width: 900px) {
  .warning-page { overflow-y: auto; height: auto; min-height: 100vh; }
  .warning-header { height: 72px; }
  .warning-back { left: 0; }
  .warning-title { font-size: 34px; }
  .warning-columns { grid-template-columns: 1fr; }
  .warning-column { min-height: 520px; }
  .warning-detail { width: 100%; }
  .warning-detail__facts { grid-template-columns: 1fr 1fr; }
  .warning-workflow-layer { padding: 16px; }
  .warning-workflow-dialog,
  .warning-workflow-dialog--handling { width: 100%; }
  .warning-workflow-form { padding: 16px; }
}
</style>
